import { css, keyframes } from "@emotion/react";
import type React from "react";
import { useThemeState } from "../hooks/useThemeState";

// Modern loading dots with smooth animation
export type LoadingDotsProps = {
	size?: number;
	color?: string;
	spacing?: number;
	className?: string;
};

const bounceAnimation = keyframes`
    0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.5;
    }
    40% {
        transform: scale(1.2);
        opacity: 1;
    }
`;

const pulseAnimation = keyframes`
    0%, 100% {
        transform: scale(1);
        opacity: 0.7;
    }
    50% {
        transform: scale(1.3);
        opacity: 1;
    }
`;

export const LoadingDots: React.FC<LoadingDotsProps> = ({
	size = 16,
	color,
	spacing = 4,
	className,
}) => {
	const themeState = useThemeState();
	const resolvedColor = color ?? (themeState.isDark() ? "#ffffff" : "#000000");
	return (
		<div
			className={className}
			css={css`
				display: flex;
				align-items: center;
				justify-content: center;
				gap: ${spacing}px;
			`}
		>
			{[0, 1, 2].map((index) => (
				<div
					key={index}
					css={css`
						width: ${size}px;
						height: ${size}px;
						border-radius: 50%;
						background-color: ${resolvedColor};
						animation: ${bounceAnimation} 1.4s ease-in-out infinite;
						animation-delay: ${index * 0.16}s;
						position: relative;
						&::before {
							content: "";
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							border-radius: 50%;
							background-color: inherit;
							animation: ${pulseAnimation} 1.4s ease-in-out infinite;
							animation-delay: ${index * 0.16 + 0.7}s;
							opacity: 0.3;
						}
					`}
				/>
			))}
		</div>
	);
};
